import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import "./LiveTrainingRecordInstructionsDialog.css";

function tableOptionsFromRows(tables) {
  const nums = new Set();
  for (const row of tables || []) {
    const n = Number(row?.tableNumber ?? row?.table_number);
    if (Number.isFinite(n) && n > 0) nums.add(n);
  }
  return [...nums].sort((a, b) => a - b);
}

/**
 * Coach picks a table camera and starts/stops an exercise-instruction recording.
 */
export function LiveTrainingRecordInstructionsDialog({
  open,
  onClose,
  tables = [],
  status = "idle",
  error = null,
  tableNumber = null,
  onStart,
  onStop,
  onResetError,
}) {
  const { t } = useTranslation();
  const options = useMemo(() => tableOptionsFromRows(tables), [tables]);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (!open) return;
    if (tableNumber != null && Number(tableNumber) > 0) {
      setSelected(String(tableNumber));
      return;
    }
    if (options.length && !selected) {
      setSelected(String(options[0]));
    }
  }, [open, tableNumber, options, selected]);

  const recording = status === "recording" || status === "starting";
  const stopping = status === "stopping";
  const busy = recording || stopping;

  const statusLabel = (() => {
    if (status === "starting") {
      return t("liveTrainingInstructionStarting", "Starting…");
    }
    if (status === "recording") {
      return t("liveTrainingInstructionRecording", "Recording…");
    }
    if (status === "stopping") {
      return t("liveTrainingInstructionStopping", "Stopping…");
    }
    if (status === "error" && error) return error;
    return t(
      "liveTrainingInstructionHint",
      "Record the coach explanation on the selected table camera. Stop when finished — the clip is saved to this exercise’s media."
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      className="lt-record-instructions-dialog"
      aria-labelledby="lt-record-instructions-title"
      PaperProps={{
        className: "lt-record-instructions-dialog__paper",
      }}
    >
      <DialogTitle id="lt-record-instructions-title">
        <span className="lt-record-instructions-dialog__title">
          <VideocamIcon fontSize="small" />
          {t("liveTrainingRecordInstructions", "Record instructions")}
        </span>
      </DialogTitle>
      <DialogContent>
        <label
          className="lt-record-instructions-dialog__label"
          htmlFor="lt-record-instructions-table"
        >
          {t("table", "Table")}
        </label>
        <select
          id="lt-record-instructions-table"
          className="lt-record-instructions-dialog__select"
          value={selected}
          disabled={busy || options.length === 0}
          onChange={(e) => {
            onResetError?.();
            setSelected(e.target.value);
          }}
        >
          {options.length === 0 ? (
            <option value="">
              {t("liveTrainingNoTables", "No tables configured")}
            </option>
          ) : (
            options.map((n) => (
              <option key={n} value={String(n)}>
                {t("table", "Table")} {n}
              </option>
            ))
          )}
        </select>

        <p
          className={`lt-record-instructions-dialog__status${
            status === "error"
              ? " lt-record-instructions-dialog__status--error"
              : recording
                ? " lt-record-instructions-dialog__status--live"
                : ""
          }`}
          role="status"
        >
          {statusLabel}
        </p>
      </DialogContent>
      <DialogActions className="lt-record-instructions-dialog__actions">
        <button
          type="button"
          className="lt-record-instructions-dialog__btn lt-record-instructions-dialog__btn--ghost"
          onClick={onClose}
          disabled={busy}
        >
          {t("close", "Close")}
        </button>
        {recording || stopping ? (
          <button
            type="button"
            className="lt-record-instructions-dialog__btn lt-record-instructions-dialog__btn--stop"
            onClick={() => onStop?.()}
            disabled={stopping}
          >
            <StopIcon fontSize="small" />
            {stopping
              ? t("liveTrainingInstructionStopping", "Stopping…")
              : t("stop", "Stop")}
          </button>
        ) : (
          <button
            type="button"
            className="lt-record-instructions-dialog__btn lt-record-instructions-dialog__btn--start"
            onClick={() => onStart?.(Number(selected))}
            disabled={!selected || options.length === 0}
          >
            <PlayArrowIcon fontSize="small" />
            {t("start", "Start")}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
}
